import { useCallback, useEffect, useRef, useState } from "react";
import Joyride, { CallBackProps, ACTIONS, EVENTS, STATUS, Step } from "react-joyride";
import type { FeatureGuideState } from "./useFeatureGuide";

interface FeatureGuideProps {
	/** Guide state from useFeatureGuide(). */
	guide: FeatureGuideState;
	/** Joyride step definitions. */
	steps: Step[];
	/** Z-index for the overlay (default 10000). */
	zIndex?: number;
	/** Called when the tour finishes or is dismissed. */
	onComplete?: () => void;
}

// PRO-730: `useGuidedTour.next()` already awaits `waitForTarget` for the
// upcoming step's target before advancing Joyride's controlled `stepIndex`,
// but that wait is a best-effort budget (it resolves either way). If a step
// effect (e.g. a Chakra Drawer animation, or a slow-mounting subtree) takes
// longer than that budget, Joyride still ends up measuring a not-yet-mounted
// target and fires TARGET_NOT_FOUND. Killing the tour on the very first such
// event turns a transient "not mounted *yet*" into "the whole tour closes",
// which is indistinguishable from a genuinely broken/missing target. Retry
// for a bounded budget — remounting Joyride so it re-measures the same
// step — before giving up.
const TARGET_RETRY_BUDGET_MS = 4000;
const TARGET_RETRY_INTERVAL_MS = 150;

/**
 * PRO-757 (author screenshot; reproduced deterministically by the coordinator) — rows of the
 * Add Access picker painted OUTSIDE the modal, on the dark overlay, but ONLY after a tour had
 * been run.
 *
 * Root cause is in react-joyride 2.9.3 itself (`getScrollParent`,
 * node_modules/react-joyride/dist/index.js:158-175 — read directly, the function is not
 * exported):
 *
 * ```js
 * const hasScrolling = parent.scrollHeight > parent.offsetHeight;
 * if (!hasScrolling && !skipFix) {
 *   parent.style.overflow = "initial";   // ← written inline, never restored
 *   return scrollDocument();
 * }
 * ```
 *
 * So when a step's target sits inside a scroll container that is NOT CURRENTLY overflowing,
 * joyride permanently neutralises that container's `overflow` so it can scroll the document
 * instead. Nothing in the library ever puts it back — `componentWillUnmount` (line 1150) only
 * detaches listeners.
 *
 * That is exactly the reported sequence: the Add Access modal's FIRST step is short, so the
 * modal body does not overflow and joyride writes `overflow: initial` onto it; the tour ends
 * (our overlay teardown works — measured overlays: 0) but the inline style stays; the admin
 * then moves to the picker step, whose ~1484px of lists no longer have a scroll container to
 * live in, so the body grows to full content height and the rows render past the dialog's
 * bottom edge. Running the picker step's own cascade tour does NOT leak, because there the
 * body already overflows (`hasScrolling` true) and joyride takes the untouched branch —
 * matching the coordinator's observation exactly.
 *
 * Fixed here rather than by passing joyride's own `disableScrollParentFix`: that option flips
 * every in-modal tour from `getElementPosition`'s accurate `getBoundingClientRect` branch
 * (index.js:237-245) to its `offsetTop - scrollParent.scrollTop` branch (233-236) — the very
 * arithmetic PRO-757 already spent three attempts stabilising via
 * `ModalProps.neutralizeInternalPositioning`. Restoring the style leaves joyride's positioning
 * path completely untouched, which is the smaller blast radius.
 *
 * `overflow: initial` is joyride's signature and the whole match condition: nothing in this
 * codebase sets that exact value inline (grepped `src`), and the third-party inline `overflow`
 * writes that do exist are other values (react-remove-scroll's `hidden` on `body`, for one),
 * so the sweep cannot clobber a deliberate style. Deliberately NOT narrowed to a remembered
 * "before" snapshot: a snapshot taken when a SECOND guide mounts would classify a previous
 * tour's not-yet-cleaned residue as legitimate and preserve the very bug this closes.
 */
const JOYRIDE_SCROLL_PARENT_OVERFLOW = "initial";

const findScrollParentOverflowResidue = (): HTMLElement[] =>
	Array.from(document.querySelectorAll<HTMLElement>('[style*="overflow"]')).filter(
		(node) => node.style.overflow === JOYRIDE_SCROLL_PARENT_OVERFLOW
	);

/** True once `target` resolves to a real, on-screen element. */
const isTargetReady = (target: Step["target"] | undefined): boolean => {
	if (typeof target !== "string") {
		// HTMLElement refs / undefined — nothing for us to poll for.
		return true;
	}
	return document.querySelector(target) !== null;
};

/**
 * Reusable on-demand feature guide powered by react-joyride.
 *
 * Unlike the legacy GuidedTour (cookie-gated, global), this is designed
 * for contextual "Show Me" tours triggered by the user from within a feature.
 *
 * Usage:
 * ```tsx
 * const guide = useFeatureGuide();
 * <button onClick={guide.start}>Show Me</button>
 * <FeatureGuide guide={guide} steps={panelMeetingSteps} />
 * ```
 */
export const FeatureGuide: React.FC<FeatureGuideProps> = ({
	guide,
	steps,
	onComplete,
	zIndex = 10000,
}) => {
	// Forces Joyride to fully remount (and so re-measure `step.target`) once a
	// bounded retry sees the target has appeared, without changing `stepIndex`.
	const [remountKey, setRemountKey] = useState(0);
	// Tracks the in-flight retry: which step it's for and when to give up.
	const retryRef = useRef<{ stepIndex: number; deadline: number } | null>(null);
	const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const clearRetry = () => {
		if (retryTimerRef.current !== null) {
			clearTimeout(retryTimerRef.current);
			retryTimerRef.current = null;
		}
		retryRef.current = null;
	};

	// Never leave a pending retry poll running past unmount.
	useEffect(() => clearRetry, []);

	const restoreScrollParentOverflow = useCallback(() => {
		findScrollParentOverflowResidue().forEach((node) => {
			node.style.removeProperty("overflow");
			if (node.getAttribute("style") === "") {
				node.removeAttribute("style");
			}
		});
	}, []);

	/*
		Every exit path, not just the happy one — the overlay leak this flow already fixed once
		is the cautionary tale (it covered the path that was tested and this one leaked anyway):
		  - not-running covers Got it/FINISHED, Skip tour/SKIPPED, the tooltip X and Escape
		    (both ACTIONS.CLOSE), and an owner calling `stop()` because its own step changed
		    underneath the tour — all of them land on `guide.isRunning === false`, and it also
		    sweeps a previous tour's residue on mount;
		  - unmount covers a guide torn out of the tree mid-run (e.g. its modal closing), where
		    no callback ever fires, and runs strictly after Joyride's own `componentWillUnmount`
		    has detached the listeners that could otherwise re-write the style.
		Both are needed: `useGuidedTour` unmounts Joyride as soon as a tour ends, but
		`InstructionsInfoBox` keeps its `FeatureGuide` mounted across tours.
	*/
	useEffect(() => {
		if (!guide.isRunning) {
			restoreScrollParentOverflow();
		}
	}, [guide.isRunning, restoreScrollParentOverflow]);

	useEffect(() => restoreScrollParentOverflow, [restoreScrollParentOverflow]);

	const stopTour = () => {
		clearRetry();
		guide.stop();
		onComplete?.();
	};

	const scheduleRetry = (stepIndex: number, target: Step["target"] | undefined) => {
		if (retryRef.current === null || retryRef.current.stepIndex !== stepIndex) {
			retryRef.current = { stepIndex, deadline: Date.now() + TARGET_RETRY_BUDGET_MS };
		}
		const { deadline } = retryRef.current;

		retryTimerRef.current = setTimeout(() => {
			if (isTargetReady(target)) {
				clearRetry();
				setRemountKey((key) => key + 1);
				return;
			}
			if (Date.now() >= deadline) {
				stopTour();
				return;
			}
			scheduleRetry(stepIndex, target);
		}, TARGET_RETRY_INTERVAL_MS);
	};

	const handleCallback = (data: CallBackProps) => {
		const { status, type, action, index } = data;

		if (action === ACTIONS.CLOSE) {
			stopTour();
			return;
		}

		if (([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status)) {
			stopTour();
			return;
		}

		if (type === EVENTS.STEP_AFTER) {
			clearRetry();
			if (action === ACTIONS.NEXT) {
				guide.next();
			} else if (action === ACTIONS.PREV) {
				guide.prev();
			}
			return;
		}

		// A step's target can legitimately not be mounted *yet* (e.g. a Chakra
		// Drawer animation, or an async effect triggered by the previous step's
		// `onAfterStep`) even though `useGuidedTour` already waited for it —
		// that wait is a best-effort budget, not a guarantee. Retry for a
		// bounded window (re-measuring via a Joyride remount) before treating
		// the target as genuinely absent — only a target that's still missing
		// once that budget is exhausted warrants tearing the whole tour down
		// (it would otherwise get stuck mid-tour with spotlightClicks={false},
		// blocking all page clicks indefinitely).
		if (type === EVENTS.TARGET_NOT_FOUND) {
			scheduleRetry(index, data.step.target);
		}
	};

	return (
		<Joyride
			key={remountKey}
			steps={steps}
			run={guide.isRunning}
			stepIndex={guide.stepIndex}
			continuous
			showProgress
			showSkipButton
			callback={handleCallback}
			scrollToFirstStep
			scrollOffset={100}
			disableOverlayClose
			spotlightClicks={false}
			spotlightPadding={6}
			styles={{
				options: {
					arrowColor: "#fff",
					backgroundColor: "#fff",
					overlayColor: "rgba(0, 0, 0, 0.4)",
					primaryColor: "#4F46E5",
					spotlightShadow: "0 0 0 4px rgba(79, 70, 229, 0.6)",
					textColor: "#333",
					width: 380,
					zIndex,
				},
				spotlight: {
					borderRadius: "6px",
				},
			}}
			locale={{
				back: "Back",
				close: "Got it",
				last: "Got it",
				next: "Next",
				skip: "Skip tour",
			}}
			floaterProps={{
				disableAnimation: false,
			}}
		/>
	);
};
